import React, { useCallback, useEffect, useState } from "react";
import i18next from "i18next";
import { useSelector } from "react-redux";
import { PageBody, PageHeader } from "components";
import orginizationService from "services/orginization-profile.service";
import { selectOrginizationDetails } from "redux/slices/orginizationSlice";
import { isAdminAccount } from "redux/slices/accountSlice";
import css from "./domains.module.scss";

const DOMAIN_REGEX = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;

export default function TenantDomainsPage() {
  const organization = useSelector(selectOrginizationDetails);
  const isAdmin = useSelector(isAdminAccount);
  const enabled = Boolean(organization?.domainInviteEnabled);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(enabled && isAdmin);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await orginizationService.getTenantDomains();
      setDomains(
        (Array.isArray(response?.domains) ? response.domains : []).map(
          (value, index) => ({ id: `${value}-${index}`, value, error: "" })
        )
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          i18next.t("labels.domains_load_failed")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled && isAdmin) loadDomains();
  }, [enabled, isAdmin, loadDomains]);

  const handleAdd = () => {
    setDomains((current) => [
      ...current,
      { id: `new-${Date.now()}-${Math.random()}`, value: "", error: "" },
    ]);
    setSuccess("");
  };

  const handleChange = (id, value) => {
    setDomains((current) =>
      current.map((row) => (row.id === id ? { ...row, value, error: "" } : row))
    );
    setSuccess("");
  };

  const handleRemove = (id) => {
    setDomains((current) => current.filter((row) => row.id !== id));
    setSuccess("");
  };

  const handleSave = async () => {
    const normalized = domains.map((row) => row.value.trim().toLowerCase());
    const seen = new Set();
    let hasErrors = false;
    const validated = domains.map((row, index) => {
      const value = normalized[index];
      let rowError = "";
      if (!value) {
        rowError = i18next.t("labels.domain_empty");
      } else if (!DOMAIN_REGEX.test(value)) {
        rowError = i18next.t("labels.domain_invalid");
      } else if (seen.has(value)) {
        rowError = i18next.t("labels.domain_duplicate");
      } else {
        seen.add(value);
      }
      if (rowError) hasErrors = true;
      return { ...row, error: rowError };
    });

    if (hasErrors) {
      setDomains(validated);
      setError(i18next.t("labels.domain_validation_failed"));
      setSuccess("");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await orginizationService.saveTenantDomains(normalized);
      setSuccess(i18next.t("labels.domains_saved"));
      await loadDomains();
    } catch (saveError) {
      const fieldErrors = saveError.response?.data?.fieldErrors;
      if (fieldErrors) {
        setDomains(
          validated.map((row, index) => ({
            ...row,
            error: fieldErrors[index] || row.error,
          }))
        );
      }
      setError(
        saveError.response?.data?.message ||
          i18next.t("labels.domains_save_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={css.page}>
      <PageHeader title={i18next.t("labels.domains")} />
      <PageBody padding="32px">
        {!enabled ? (
          <div className={css.message}>
            {i18next.t("labels.domain_invites_not_enabled")}
          </div>
        ) : !isAdmin ? (
          <div className={css.message}>
            {i18next.t("labels.domain_admin_only")}
          </div>
        ) : loading ? (
          <div className={css.message}>{i18next.t("labels.loading_domains")}</div>
        ) : (
          <section className={css.card}>
            <header className={css.header}>
              <div>
                <h2>{i18next.t("labels.domain_configurations")}</h2>
                <p>{i18next.t("labels.domain_configurations_help")}</p>
              </div>
              <button type="button" className={css.addButton} onClick={handleAdd}>
                + {i18next.t("labels.add_domain")}
              </button>
            </header>
            <div className={css.domainList}>
              {domains.length === 0 ? (
                <div className={css.empty}>{i18next.t("labels.no_domains")}</div>
              ) : (
                domains.map((row) => (
                  <div className={css.domainRow} key={row.id}>
                    <div className={css.inputGroup}>
                      <input
                        aria-label={i18next.t("labels.domain")}
                        value={row.value}
                        onChange={(event) =>
                          handleChange(row.id, event.target.value)
                        }
                        placeholder={i18next.t("labels.domain_placeholder")}
                        aria-invalid={Boolean(row.error)}
                      />
                      {row.error && (
                        <span className={css.fieldError}>{row.error}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={css.removeButton}
                      onClick={() => handleRemove(row.id)}
                      aria-label={i18next.t("labels.remove_domain")}
                    >
                      {i18next.t("labels.remove")}
                    </button>
                  </div>
                ))
              )}
            </div>
            {error && <div className={css.error} role="alert">{error}</div>}
            {success && <div className={css.success} role="status">{success}</div>}
            <footer className={css.footer}>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || domains.some((row) => row.error)}
              >
                {saving
                  ? i18next.t("labels.saving_domains")
                  : i18next.t("labels.save_domains")}
              </button>
            </footer>
          </section>
        )}
      </PageBody>
    </div>
  );
}
